import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { trainingPlans, couchTo5KSchedule, halHigdonInt1Schedule, runTypes } from '../plansData';
import useLocalStorage from '../hooks/useLocalStorage';
import { PLAN_IDS } from '../constants';
import { useSettings } from './SettingsContext';
import LZString from 'lz-string';
import { encodePlanData, decodePlanData } from '../utils/urlEncoder';

const RunPlanContext = createContext(null);

export const useRunPlan = () => {
  const context = useContext(RunPlanContext);
  if (!context) {
    throw new Error('useRunPlan must be used within RunPlanProvider');
  }
  return context;
};

export const RunPlanProvider = ({ children }) => {
  // Get settings from SettingsContext
  // Note: SettingsProvider must wrap RunPlanProvider in the component tree
  const settings = useSettings();

  // Plan and run state
  const [selectedPlan, setSelectedPlan] = useLocalStorage('selectedPlan', null);
  const [runs, setRuns] = useLocalStorage('runs', {});
  const [showPlanChoice, setShowPlanChoice] = useLocalStorage('showPlanChoice', true);
  const [trainingGoal, setTrainingGoal] = useLocalStorage('trainingGoal', null);
  const [selectedRaceDistance, setSelectedRaceDistance] = useLocalStorage('selectedRaceDistance', null);

  // Multi-plan storage
  const [savedPlans, setSavedPlans] = useLocalStorage('savedPlans', []);

  // Calendar offset for earlier weeks
  const [earlierWeeksOffset, setEarlierWeeksOffset] = useLocalStorage('earlierWeeksOffset', 0);

  // Hidden weeks (stores week indices that user has manually hidden)
  const [hiddenWeeks, setHiddenWeeks] = useLocalStorage('hiddenWeeks', []);

  // UI state (not persisted)
  const [activeView, setActiveView] = useState('calendar');
  const [showAddRun, setShowAddRun] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingRun, setEditingRun] = useState(null);
  const [showDuplicateMenu, setShowDuplicateMenu] = useState(false);
  const [weekToDuplicate, setWeekToDuplicate] = useState(null);
  const [previewPlanData, setPreviewPlanData] = useState(null);

  // Use settings from context or provide defaults for backward compatibility
  const weekStartsMonday = settings?.weekStartsMonday ?? true;
  const theme = settings?.theme ?? 'blue';
  const t = settings?.t ?? { primary: 'bg-sky-500', hover: 'hover:bg-sky-600', text: 'text-sky-600', grad: 'from-sky-400 to-blue-500' };
  const themes = settings?.themes ?? {};
  const darkMode = settings?.darkMode ?? false;
  const verticalLayout = settings?.verticalLayout ?? false;
  const abbreviatedWeekView = settings?.abbreviatedWeekView ?? true;
  const showRunEmojis = settings?.showRunEmojis ?? false;
  const c25kViewMode = settings?.c25kViewMode ?? 'time';
  const distancePresets = settings?.distancePresets ?? [3, 6, 10];
  const restDayIcon = settings?.restDayIcon ?? 'sleep';

  const plans = trainingPlans;
  const types = runTypes;

  // Find the earliest date with a run
  const getEarliestRunDate = useCallback(() => {
    if (!runs || Object.keys(runs).length === 0) return null;

    const dates = Object.keys(runs).map(dateString => new Date(dateString));
    return new Date(Math.min(...dates));
  }, [runs]);

  // Calculate weeks based on selected plan, starting from earliest run date
  const getWeeks = useCallback(() => {
    const today = new Date();
    const earliestRun = getEarliestRunDate();

    // Determine start date: either earliest run or current week
    let start;
    if (earliestRun) {
      // Start from the week containing the earliest run
      start = new Date(earliestRun);
      const day = earliestRun.getDay();
      const diff = weekStartsMonday ? (day === 0 ? -6 : 1 - day) : -day;
      start.setDate(earliestRun.getDate() + diff);
    } else {
      // No runs yet, start from current week
      start = new Date(today);
      const day = today.getDay();
      const diff = weekStartsMonday ? (day === 0 ? -6 : 1 - day) : -day;
      start.setDate(today.getDate() + diff);
    }

    // Apply earlier weeks offset (move start date backwards)
    start.setDate(start.getDate() - (earlierWeeksOffset * 7));

    // Calculate number of weeks to show
    // From start date to end of current week + plan weeks
    const currentWeekStart = new Date(today);
    const currentDay = today.getDay();
    const currentDiff = weekStartsMonday ? (currentDay === 0 ? -6 : 1 - currentDay) : -currentDay;
    currentWeekStart.setDate(today.getDate() + currentDiff);

    // Calculate weeks from start to current week
    const weeksDiff = Math.ceil((currentWeekStart - start) / (7 * 24 * 60 * 60 * 1000));
    const planWeeks = selectedPlan ? selectedPlan.weeks.length : 2;

    // Show at least from earliest run to current + plan weeks, minimum of plan weeks
    const num = Math.max(weeksDiff + planWeeks, planWeeks);

    const weeks = [];
    for (let w = 0; w < num; w++) {
      const ws = new Date(start);
      ws.setDate(start.getDate() + w * 7);
      const days = [];
      for (let d = 0; d < 7; d++) {
        const date = new Date(ws);
        date.setDate(ws.getDate() + d);
        days.push(date);
      }
      weeks.push(days);
    }
    return weeks;
  }, [weekStartsMonday, selectedPlan, getEarliestRunDate, earlierWeeksOffset]);

  const [weeks, setWeeks] = useState(getWeeks());

  useEffect(() => {
    setWeeks(getWeeks());
  }, [getWeeks]);

  // Add a run
  const addRun = (date, data) => {
    setRuns(prev => ({ ...prev, [date.toDateString()]: data }));
    setShowAddRun(false);
    setEditingRun(null);
  };

  // Populate runs from a plan
  const populate = (plan, customStartDate = null, preserveCompleted = false, userPreferences = null) => {
    const newRuns = preserveCompleted ? { ...runs } : {};

    // Map day IDs to day indices (0=Monday if weekStartsMonday, 0=Sunday otherwise)
    const dayIdToIndex = (dayId) => {
      const dayMap = {
        'monday': weekStartsMonday ? 0 : 1,
        'tuesday': weekStartsMonday ? 1 : 2,
        'wednesday': weekStartsMonday ? 2 : 3,
        'thursday': weekStartsMonday ? 3 : 4,
        'friday': weekStartsMonday ? 4 : 5,
        'saturday': weekStartsMonday ? 5 : 6,
        'sunday': weekStartsMonday ? 6 : 0
      };
      return dayMap[dayId];
    };

    if (preserveCompleted && customStartDate && typeof customStartDate === 'string') {
      const [year, month, day] = customStartDate.split('-').map(Number);
      const cutoffDate = new Date(year, month - 1, day);
      cutoffDate.setHours(0, 0, 0, 0);

      Object.keys(newRuns).forEach(dateString => {
        const runDate = new Date(dateString);
        runDate.setHours(0, 0, 0, 0);
        const run = newRuns[dateString];
        if (runDate >= cutoffDate && run.status !== 'completed') {
          delete newRuns[dateString];
        }
      });
    }

    let today;
    if (customStartDate && typeof customStartDate === 'string') {
      const [year, month, day] = customStartDate.split('-').map(Number);
      today = new Date(year, month - 1, day);
    } else {
      today = new Date();
    }
    const day = today.getDay();
    const diff = weekStartsMonday ? (day === 0 ? -6 : 1 - day) : -day;
    const start = new Date(today);
    start.setDate(today.getDate() + diff);

    if (plan.id === PLAN_IDS.C25K) {
      couchTo5KSchedule.forEach(({ day: dayOffset, type, notes, timeFormat, distance }) => {
        const rd = new Date(start);
        rd.setDate(start.getDate() + dayOffset);
        const dateKey = rd.toDateString();
        if (!preserveCompleted || !newRuns[dateKey]) {
          newRuns[dateKey] = {
            distance: distance,
            type: type,
            notes: notes,
            timeFormat: timeFormat,
            status: 'planned',
            id: Date.now() + dayOffset
          };
        }
      });
    } else if (plan.id === 7) {
      halHigdonInt1Schedule.forEach(({ day: dayNumber, type, notes, distance }) => {
        const rd = new Date(start);
        rd.setDate(start.getDate() + (dayNumber - 1));
        const dateKey = rd.toDateString();
        if (distance > 0 || type === 'recovery' || type === 'cross') {
          if (!preserveCompleted || !newRuns[dateKey]) {
            newRuns[dateKey] = {
              distance: distance,
              type: type,
              notes: notes,
              status: 'planned',
              id: Date.now() + dayNumber
            };
          }
        }
      });
    } else {
      // Get user's preferred days if available
      let preferredDayIndices = null;
      if (userPreferences?.selectedDays && userPreferences.selectedDays.length >= plan.runs) {
        // Convert day IDs to indices and sort them
        preferredDayIndices = userPreferences.selectedDays
          .map(dayIdToIndex)
          .sort((a, b) => a - b);

        // Take only the number of days needed for the plan
        preferredDayIndices = preferredDayIndices.slice(0, plan.runs);
      }

      plan.weeks.forEach((wm, wi) => {
        const mpr = wm / plan.runs;
        for (let ri = 0; ri < plan.runs; ri++) {
          let dayOffset;

          if (preferredDayIndices) {
            // Use user's preferred days
            dayOffset = preferredDayIndices[ri];
          } else {
            // Default: evenly distribute runs throughout the week
            dayOffset = Math.floor((7 / plan.runs) * ri);
          }

          const off = wi * 7 + dayOffset;
          const rd = new Date(start);
          rd.setDate(start.getDate() + off);
          const dateKey = rd.toDateString();
          let rt = 'easy';
          let dist = mpr;
          if (ri === plan.runs - 1) { rt = 'long'; dist = mpr * 1.5; }
          else if (ri === Math.floor(plan.runs / 2)) { rt = 'workout'; dist = mpr * 0.8; }
          if (!preserveCompleted || !newRuns[dateKey]) {
            newRuns[dateKey] = { distance: Math.round(dist * 10) / 10, type: rt, notes: '', status: 'planned', id: Date.now() + ri + wi * 100 };
          }
        }
      });
    }

    setRuns(newRuns);
  };

  // Create a custom plan
  const createCustomPlan = (startDate = null) => {
    const customPlan = {
      id: PLAN_IDS.CUSTOM,
      name: "Custom Plan",
      desc: "Your personalized training plan",
      weeks: [0, 0, 0, 0, 0, 0],
      diff: "Custom",
      runs: 0
    };

    if (startDate && typeof startDate === 'string') {
      const [year, month, day] = startDate.split('-').map(Number);
      const cutoffDate = new Date(year, month - 1, day);
      cutoffDate.setHours(0, 0, 0, 0);
      const updatedRuns = { ...runs };

      Object.keys(updatedRuns).forEach(dateString => {
        const runDate = new Date(dateString);
        runDate.setHours(0, 0, 0, 0);
        const run = updatedRuns[dateString];
        if (runDate >= cutoffDate && run.status !== 'completed') {
          delete updatedRuns[dateString];
        }
      });

      setRuns(updatedRuns);
    }

    // Reset earlier weeks offset when creating new plan
    setEarlierWeeksOffset(0);
    setSelectedPlan(customPlan);
    setShowPlanChoice(false);
    setActiveView('calendar');
  };

  // Add a new week to the plan (at the end)
  const addNewWeek = () => {
    if (selectedPlan) {
      const updatedPlan = {
        ...selectedPlan,
        weeks: [...selectedPlan.weeks, 0]
      };
      setSelectedPlan(updatedPlan);
    }
  };

  // Add an earlier week (at the beginning)
  const addEarlierWeek = () => {
    // Increment the offset to shift calendar start backwards
    setEarlierWeeksOffset(prev => prev + 1);

    // Also add to plan weeks
    if (selectedPlan) {
      const updatedPlan = {
        ...selectedPlan,
        weeks: [0, ...selectedPlan.weeks]
      };
      setSelectedPlan(updatedPlan);
    }
  };

  // Delete a run
  const deleteRun = (date) => {
    setRuns(prev => { const n = { ...prev }; delete n[date.toDateString()]; return n; });
    setEditingRun(null);
  };

  // Delete a week and all its runs
  const deleteWeek = (weekIndex) => {
    const weekToDelete = weeks[weekIndex];
    if (!weekToDelete) return;

    // Delete all runs in this week
    const updatedRuns = { ...runs };
    weekToDelete.forEach(day => {
      delete updatedRuns[day.toDateString()];
    });
    setRuns(updatedRuns);

    // Remove week from plan
    if (selectedPlan && selectedPlan.weeks.length > 1) {
      const updatedPlan = {
        ...selectedPlan,
        weeks: selectedPlan.weeks.filter((_, i) => i !== weekIndex)
      };
      setSelectedPlan(updatedPlan);
    }

    // Remove from hidden weeks if it was hidden
    setHiddenWeeks(prev => prev.filter(i => i !== weekIndex).map(i => i > weekIndex ? i - 1 : i));
  };

  // Hide a week from view (doesn't delete runs)
  const hideWeek = (weekIndex) => {
    if (!hiddenWeeks.includes(weekIndex)) {
      setHiddenWeeks(prev => [...prev, weekIndex]);
    }
  };

  // Unhide a week
  const unhideWeek = (weekIndex) => {
    setHiddenWeeks(prev => prev.filter(i => i !== weekIndex));
  };

  // Check if a week is empty (no runs)
  const isWeekEmpty = (weekIndex) => {
    const week = weeks[weekIndex];
    if (!week) return true;
    return week.every(day => !runs[day.toDateString()]);
  };

  // Duplicate a week
  const duplicateWeek = (weekIndex, position) => {
    const sourceWeek = weeks[weekIndex];
    const newRuns = { ...runs };

    if (position === 'next') {
      const targetWeekStart = new Date(sourceWeek[0]);
      targetWeekStart.setDate(targetWeekStart.getDate() + 7);

      sourceWeek.forEach((day, dayIndex) => {
        const sourceRun = runs[day.toDateString()];
        if (sourceRun) {
          const targetDate = new Date(targetWeekStart);
          targetDate.setDate(targetWeekStart.getDate() + dayIndex);
          newRuns[targetDate.toDateString()] = {
            ...sourceRun,
            id: Date.now() + dayIndex,
            status: 'planned'
          };
        }
      });
    } else {
      const lastWeek = weeks[weeks.length - 1];
      const targetWeekStart = new Date(lastWeek[0]);
      targetWeekStart.setDate(targetWeekStart.getDate() + 7);

      sourceWeek.forEach((day, dayIndex) => {
        const sourceRun = runs[day.toDateString()];
        if (sourceRun) {
          const targetDate = new Date(targetWeekStart);
          targetDate.setDate(targetWeekStart.getDate() + dayIndex);
          newRuns[targetDate.toDateString()] = {
            ...sourceRun,
            id: Date.now() + dayIndex,
            status: 'planned'
          };
        }
      });
    }

    setRuns(newRuns);
    setShowDuplicateMenu(false);
    setWeekToDuplicate(null);

    if (selectedPlan) {
      const updatedPlan = { ...selectedPlan, weeks: [...selectedPlan.weeks, selectedPlan.weeks[weekIndex]] };
      setSelectedPlan(updatedPlan);
    }
  };

  // Share plan via URL
  const sharePlan = async () => {
    // Validate that there's a plan to share
    if (!selectedPlan) {
      alert('Please select a training plan first before sharing.');
      return null;
    }

    if (!runs || Object.keys(runs).length === 0) {
      alert('Your stash is empty! Add some runs before sharing. 🌰');
      return null;
    }

    const planData = {
      plan: selectedPlan,
      runs: runs,
    };

    try {
      // Use optimized encoding for smaller URLs
      const encoded = encodePlanData(planData);
      const compressed = LZString.compressToEncodedURIComponent(JSON.stringify(encoded));
      const shareUrl = `${window.location.origin}${window.location.pathname}?plan=${compressed}`;

      // Check if URL is too long
      if (shareUrl.length > 1900) {
        // Fallback to CSV export
        if (window.confirm('This plan is too large for URL sharing (includes too much data). Would you like to download it as a CSV file instead?')) {
          exportCSV();
        }
        return null;
      }

      // Use Web Share API if available
      if (navigator.share) {
        try {
          await navigator.share({
            title: `${selectedPlan?.name || 'Training Plan'}`,
            text: `Check out my ${selectedPlan?.name || 'training plan'}!`,
            url: shareUrl,
          });
          return shareUrl;
        } catch (err) {
          // User cancelled or share failed, fall back to copying to clipboard
          if (err.name !== 'AbortError') {
            console.log('Share failed:', err);
          }
        }
      }

      // Fallback: Copy to clipboard
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        alert('Share link copied to clipboard! You can paste it anywhere to share your plan.');
        return shareUrl;
      } else {
        // Last resort: Show the URL in a prompt
        prompt('Copy this link to share your plan:', shareUrl);
        return shareUrl;
      }
    } catch (error) {
      console.error('Failed to share plan:', error);
      alert('Failed to create share link. Please try exporting as CSV instead.');
      return null;
    }
  };

  // Save current plan to library
  const saveCurrentPlanToLibrary = (customName = null) => {
    if (!selectedPlan || !runs || Object.keys(runs).length === 0) {
      return false;
    }

    const planToSave = {
      id: Date.now().toString(),
      name: customName || selectedPlan.name || 'Saved Plan',
      plan: selectedPlan,
      runs: runs,
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    };

    setSavedPlans(prev => [...prev, planToSave]);
    return true;
  };

  // Load a saved plan
  const loadSavedPlan = (planId) => {
    const plan = savedPlans.find(p => p.id === planId);
    if (!plan) return false;

    setSelectedPlan(plan.plan);
    setRuns(plan.runs);
    setShowPlanChoice(false);
    setActiveView('calendar');
    return true;
  };

  // Delete a saved plan
  const deleteSavedPlan = (planId) => {
    setSavedPlans(prev => prev.filter(p => p.id !== planId));
  };

  // Import plan from URL - shows preview modal
  const importPlanFromURL = useCallback((compressedData) => {
    if (!compressedData || typeof compressedData !== 'string') {
      console.error('Invalid compressed data provided');
      return false;
    }

    try {
      const decompressed = LZString.decompressFromEncodedURIComponent(compressedData);
      if (!decompressed) {
        throw new Error('Failed to decompress plan data');
      }

      const encoded = JSON.parse(decompressed);

      if (!encoded || typeof encoded !== 'object') {
        throw new Error('Invalid plan data structure');
      }

      // Decode the optimized format
      const planData = decodePlanData(encoded);

      if (!planData || !planData.plan || !planData.runs) {
        throw new Error('Missing plan or runs data');
      }

      // Show preview modal instead of importing directly
      setPreviewPlanData(planData);

      return true;
    } catch (error) {
      console.error('Failed to import plan from URL:', error);
      alert('Failed to import plan from link. The link may be invalid or corrupted.');
      return false;
    }
  }, [setPreviewPlanData]);

  // Handle "Make a Copy" from preview
  const handleMakeCopy = () => {
    if (!previewPlanData) return;

    // Save current plan if it exists
    if (selectedPlan && runs && Object.keys(runs).length > 0) {
      saveCurrentPlanToLibrary(`${selectedPlan.name} (saved ${new Date().toLocaleDateString()})`);
    }

    // Load the previewed plan
    setSelectedPlan(previewPlanData.plan);
    setRuns(previewPlanData.runs);
    setShowPlanChoice(false);
    setActiveView('calendar');
    setPreviewPlanData(null);
  };

  // Handle "Use as Template" from preview
  const handleUseAsTemplate = () => {
    if (!previewPlanData) return;

    // Save current plan if it exists
    if (selectedPlan && runs && Object.keys(runs).length > 0) {
      saveCurrentPlanToLibrary(`${selectedPlan.name} (saved ${new Date().toLocaleDateString()})`);
    }

    // Load the plan but clear all run statuses (start fresh)
    const freshRuns = {};
    Object.entries(previewPlanData.runs).forEach(([date, run]) => {
      freshRuns[date] = {
        ...run,
        status: 'planned',
        notes: '',
        actualDistance: undefined
      };
    });

    setSelectedPlan(previewPlanData.plan);
    setRuns(freshRuns);
    setShowPlanChoice(false);
    setActiveView('calendar');
    setPreviewPlanData(null);
  };

  // Export runs to CSV
  const exportCSV = () => {
    const h = ['Date', 'Distance', 'Type', 'Status', 'Notes'];
    const r = Object.entries(runs).map(([d, run]) => [d, run.distance, run.type, run.status, run.notes || '']);
    const c = [h.join(','), ...r.map(row => row.map(cell => '"' + cell + '"').join(','))].join('\n');
    const b = new Blob([c], { type: 'text/csv' });
    const u = URL.createObjectURL(b);
    const l = document.createElement('a');
    l.href = u;
    l.download = 'scurry_' + new Date().toISOString().split('T')[0] + '.csv';
    document.body.appendChild(l);
    l.click();
    document.body.removeChild(l);
    URL.revokeObjectURL(u);
  };

  // Import runs from CSV
  const importCSV = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const txt = ev.target.result;
      const lines = txt.split('\n');
      const nr = {};
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        const m = line.match(/(".*?"|[^,]+)(?=\s*,|\s*$)/g);
        if (!m || m.length < 4) continue;
        const p = m.map(x => x.replace(/^"|"$/g, ''));
        nr[p[0]] = { distance: parseFloat(p[1]), type: p[2], status: p[3], notes: p[4] || '', id: Date.now() + i };
      }
      setRuns(nr);

      // Set imported plan as selected
      const importedPlan = {
        id: PLAN_IDS.IMPORTED,
        name: "Imported Plan",
        desc: "Plan imported from CSV",
        weeks: [0, 0, 0, 0, 0, 0],
        diff: "Custom",
        runs: Object.keys(nr).length
      };
      setSelectedPlan(importedPlan);

      if (e.target) {
        e.target.value = '';
      }
    };
    reader.readAsText(f);
  };

  // Get stats for a set of days
  const getStats = (wd) => {
    let completedTotal = 0, plannedTotal = 0, easy = 0;
    wd.forEach(d => {
      const r = runs[d.toDateString()];
      if (r) {
        const dist = r.distance || 0;
        plannedTotal += dist;
        if (r.status === 'completed' || r.status === 'adjusted') {
          const actualDist = r.status === 'adjusted' ? r.actualDistance : r.distance;
          completedTotal += actualDist || 0;
          if (r.type === 'easy' || r.type === 'recovery' || r.type === 'long') easy += actualDist || 0;
        }
      }
    });
    const ep = completedTotal > 0 ? Math.round((easy / completedTotal) * 100) : 0;
    return {
      total: completedTotal || 0,
      planned: plannedTotal || 0,
      easy: ep,
      hard: 100 - ep
    };
  };

  // Get analytics data
  const getLast7DaysData = () => {
    const data = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const r = runs[date.toDateString()];
      const dist = r && (r.status === 'completed' || r.status === 'adjusted') ?
        (r.status === 'adjusted' ? r.actualDistance : r.distance) : 0;
      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        miles: dist
      });
    }
    return data;
  };

  const getLast4WeeksData = () => {
    const data = [];
    const today = new Date();
    for (let w = 3; w >= 0; w--) {
      let weekTotal = 0;
      for (let d = 0; d < 7; d++) {
        const date = new Date(today);
        date.setDate(today.getDate() - (w * 7 + d));
        const r = runs[date.toDateString()];
        if (r && (r.status === 'completed' || r.status === 'adjusted')) {
          weekTotal += r.status === 'adjusted' ? r.actualDistance : r.distance;
        }
      }
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - (w * 7 + 6));
      data.push({
        week: weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        miles: Math.round(weekTotal * 10) / 10
      });
    }
    return data;
  };

  const getLast365DaysData = () => {
    const data = [];
    const today = new Date();
    for (let m = 11; m >= 0; m--) {
      let monthTotal = 0;
      const daysInPeriod = 30;
      for (let d = 0; d < daysInPeriod; d++) {
        const date = new Date(today);
        date.setDate(today.getDate() - (m * daysInPeriod + d));
        const r = runs[date.toDateString()];
        if (r && (r.status === 'completed' || r.status === 'adjusted')) {
          monthTotal += r.status === 'adjusted' ? r.actualDistance : r.distance;
        }
      }
      const monthStart = new Date(today);
      monthStart.setDate(today.getDate() - (m * daysInPeriod));
      data.push({
        month: monthStart.toLocaleDateString('en-US', { month: 'short' }),
        miles: Math.round(monthTotal * 10) / 10
      });
    }
    return data;
  };

  const getTotalMileage = (days) => {
    const today = new Date();
    let total = 0;
    for (let i = 0; i < days; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const r = runs[date.toDateString()];
      if (r && (r.status === 'completed' || r.status === 'adjusted')) {
        total += r.status === 'adjusted' ? r.actualDistance : r.distance;
      }
    }
    return Math.round(total * 10) / 10;
  };

  const value = {
    // State
    runs,
    setRuns,
    selectedPlan,
    setSelectedPlan,
    showPlanChoice,
    setShowPlanChoice,
    trainingGoal,
    setTrainingGoal,
    selectedRaceDistance,
    setSelectedRaceDistance,
    savedPlans,
    setSavedPlans,
    previewPlanData,
    setPreviewPlanData,
    activeView,
    setActiveView,
    weeks,
    showAddRun,
    setShowAddRun,
    selectedDate,
    setSelectedDate,
    editingRun,
    setEditingRun,
    showDuplicateMenu,
    setShowDuplicateMenu,
    weekToDuplicate,
    setWeekToDuplicate,

    // Settings (from SettingsContext) - read-only for backward compatibility
    weekStartsMonday,
    theme,
    darkMode,
    verticalLayout,
    abbreviatedWeekView,
    showRunEmojis,
    c25kViewMode,
    distancePresets,
    restDayIcon,
    // Settings setters (from SettingsContext if available)
    setWeekStartsMonday: settings?.setWeekStartsMonday,
    setTheme: settings?.setTheme,
    setDarkMode: settings?.setDarkMode,
    setVerticalLayout: settings?.setVerticalLayout,
    setAbbreviatedWeekView: settings?.setAbbreviatedWeekView,
    setShowRunEmojis: settings?.setShowRunEmojis,
    setC25kViewMode: settings?.setC25kViewMode,
    setDistancePresets: settings?.setDistancePresets,
    setRestDayIcon: settings?.setRestDayIcon,

    // Theme config
    t,
    themes,
    plans,
    types,

    // Functions
    addRun,
    populate,
    createCustomPlan,
    addNewWeek,
    addEarlierWeek,
    deleteRun,
    deleteWeek,
    hideWeek,
    unhideWeek,
    isWeekEmpty,
    duplicateWeek,
    sharePlan,
    importPlanFromURL,
    exportCSV,
    importCSV,
    // Week visibility
    hiddenWeeks,
    setHiddenWeeks,
    // Multi-plan functions
    saveCurrentPlanToLibrary,
    loadSavedPlan,
    deleteSavedPlan,
    handleMakeCopy,
    handleUseAsTemplate,
    // Analytics functions (kept here for backward compatibility)
    getStats,
    getLast7DaysData,
    getLast4WeeksData,
    getLast365DaysData,
    getTotalMileage,
  };

  return (
    <RunPlanContext.Provider value={value}>
      {children}
    </RunPlanContext.Provider>
  );
};
